import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout, { LegalTable } from '../components/LegalLayout';
import { COMPANY } from '../lib/company';
import { openCookieSettings } from '../lib/consent';
import { useSEO } from '../hooks/useSEO';

const sections = [
  {
    id: 'what',
    title: 'What cookies are',
    body: (
      <p>
        Cookies are small text files a website stores on your device. Similar technologies, such as browser local storage, work in the same way. In this policy &ldquo;cookies&rdquo; covers all of them. This policy explains which cookies{' '}
        {COMPANY.legalName} (trading as {COMPANY.tradingName}) uses on ip9.io and why. It should be read with our <Link to="/privacy-policy">Privacy Policy</Link>.
      </p>
    ),
  },
  {
    id: 'consent',
    title: 'Your choice',
    body: (
      <>
        <p>
          Under the UK Privacy and Electronic Communications Regulations (PECR) and the EU ePrivacy Directive, we only use cookies that are strictly necessary without asking. Analytics and marketing cookies are{' '}
          <strong>off by default</strong> and are set only if you click &ldquo;Accept all&rdquo; in our cookie banner, or switch on that category under &ldquo;Customise choices&rdquo;. Rejecting them is as easy as accepting, and it does not limit your use of the website.
        </p>
        <p>
          You can change or withdraw your consent at any time:{' '}
          <button type="button" onClick={openCookieSettings} className="underline underline-offset-2 text-cyan-400 hover:text-cyan-300">
            open cookie settings
          </button>
          , or use the &ldquo;Cookie settings&rdquo; link in the footer of every page. When you withdraw consent, we stop the tools in that category and delete their cookies from this domain. We ask for your choice again after 12 months.
        </p>
      </>
    ),
  },
  {
    id: 'necessary',
    title: 'Strictly necessary cookies',
    body: (
      <>
        <p>These are needed for the website to work or to remember choices you ask us to remember. They do not require consent.</p>
        <LegalTable
          head={['Name', 'Provider', 'Purpose', 'Duration']}
          rows={[
            ['ip9-cookie-consent (local storage)', 'IP9', 'Remembers your cookie choice', '12 months'],
            ['preferred-language (local storage)', 'IP9', 'Remembers the language you selected', 'Until you clear it'],
            ['__cf_bm, cf_clearance', 'Cloudflare, Inc.', 'Protects the site from malicious bots and attacks. Set only when Cloudflare security checks are active', '30 minutes / up to 1 year'],
          ]}
        />
        <p>The customer dashboard at dashboard.ip9.io uses its own strictly necessary session cookies to keep you signed in and secure.</p>
      </>
    ),
  },
  {
    id: 'analytics',
    title: 'Analytics cookies (only with consent)',
    body: (
      <>
        <p>These help us understand how visitors use the website so we can improve it. We do not use them for advertising.</p>
        <LegalTable
          head={['Name', 'Provider', 'Purpose', 'Duration']}
          rows={[
            ['_ga', 'Google Analytics (Google Ireland Limited / Google LLC)', 'Distinguishes visitors to produce aggregated usage statistics', '2 years'],
            ['_ga_<container-id>', 'Google Analytics', 'Keeps session state for usage statistics', '2 years'],
            ['ph_<project-key>_posthog', 'PostHog, Inc.', 'Product analytics: page views, interactions and session replays (form inputs masked)', '1 year'],
          ]}
        />
        <p>
          Google and PostHog may process this data in the United States. See the safeguards described in our <Link to="/privacy-policy#transfers">Privacy Policy</Link>, and the providers&rsquo; own policies:{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google</a>,{' '}
          <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer">PostHog</a>.
        </p>
      </>
    ),
  },
  {
    id: 'marketing',
    title: 'Marketing cookies (only with consent)',
    body: (
      <>
        <p>
          We use the Meta Pixel to measure how many visitors arrive from our Facebook and Instagram adverts, to understand which adverts work, and to show relevant IP9 adverts to people who have visited our website. The pixel sends Meta the pages you visit on ip9.io together with
          your browser and device information and IP address. If you are logged in to Facebook or Instagram, Meta can link this to your account.
        </p>
        <LegalTable
          head={['Name', 'Provider', 'Purpose', 'Duration']}
          rows={[
            ['_fbp', 'Meta Platforms Ireland Limited', 'Identifies the browser to measure ad visits and conversions', '90 days'],
            ['_fbc', 'Meta Platforms Ireland Limited', 'Stores the click identifier when you arrive from a Meta advert', '90 days'],
            ['fr (on facebook.com)', 'Meta Platforms Ireland Limited', 'Delivers and measures adverts; set by Meta when you are logged in to Facebook', '90 days'],
          ]}
        />
        <p>
          For data collected by the pixel, we and Meta Platforms Ireland Limited are joint controllers for the collection and transmission of the data to Meta; Meta is responsible for its own later processing. The arrangement is described in Meta&rsquo;s{' '}
          <a href="https://www.facebook.com/legal/controller_addendum" target="_blank" rel="noopener noreferrer">Controller Addendum</a>. Meta may transfer data to the United States. See the{' '}
          <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer">Meta Privacy Policy</a> and manage your ad preferences in your Facebook or Instagram settings.
        </p>
      </>
    ),
  },
  {
    id: 'browser',
    title: 'Controlling cookies in your browser',
    body: (
      <p>
        Most browsers let you view, block and delete cookies in their settings. Blocking strictly necessary cookies may stop parts of the website or dashboard from working. You can also install the{' '}
        <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics opt-out browser add-on</a>.
      </p>
    ),
  },
  {
    id: 'changes',
    title: 'Changes and contact',
    body: (
      <p>
        If we add or change cookies, we will update this page and, where consent is needed, ask you again. Questions: <a href={`mailto:${COMPANY.emails.privacy}`}>{COMPANY.emails.privacy}</a>.
      </p>
    ),
  },
];

const CookiePolicyPage = () => {
  useSEO('cookiePolicy');
  return <LegalLayout title="Cookie Policy" sections={sections} />;
};

export default CookiePolicyPage;
